import { monotonicFactory } from 'ulidx'
import type { PRNG } from 'ulidx'

import { gpCorrectedNow } from './corrected-clock'

/** Whatever tells the generator what time it is. Narrower than CorrectedClock: a generator has no business feeding one. */
export interface IdGeneratorClock {
    now(): number;
}

/**
 * A generator whose ids are ever-increasing, because it mints them from one
 * monotonic sequence — a second sequence over the same clock mints ids out of
 * order (#667).
 *
 * Its high-water mark lives for as long as the generator does, so a spec that
 * pins the clock to the past needs its own generator rather than the page's:
 * the page's has already been carried to the real clock by whatever minted an
 * id first, and would clamp every pinned reading to that.
 */
export function createUniqueIdGenerator(clock: IdGeneratorClock, prng?: PRNG) {

    const mint = monotonicFactory(prng);

    return function generateUniqueId(prefix?: string): string {

        // Server time, not this machine's. A ulid's timestamp is read back out
        // by pigeon and ordered against ids minted elsewhere, including by the
        // server, so a local clock that disagreed would order changes wrong
        // (#763).
        const result = mint(clock.now());  // 01BX5ZZKBKACTAV9WEVGEMMVRZ

        if (prefix) {
            return `${prefix}_${result}`
        } else {
            return result;
        }
    };
}

let _generateForThisPage = createUniqueIdGenerator({ now: gpCorrectedNow });

export function gpGenerateUniqueId(prefix?: string): string {
    return _generateForThisPage(prefix);
}

/**
 * Test seam: the merge corpus generator installs a seeded generator over a
 * fake clock so a recorded history is reproducible from its seed. Nothing in
 * the app calls this.
 */
export function __setUniqueIdGeneratorForTests(gen: ((prefix?: string) => string) | null): void {
    _generateForThisPage = gen ?? createUniqueIdGenerator({ now: gpCorrectedNow });
}
